/**
 * Substrings that mark a key as carrying personal data. Matched against the key with separators
 * and case removed, so `emailAddr`, `email_address`, and `EmailAddr` all match `email`.
 */
const PII_TOKENS = ['email', 'phone', 'token', 'secret', 'firstname', 'lastname', 'fullname'] as const

/** Serialized characters a single logged value may occupy before it is truncated. */
export const MAX_SERIALIZED_LENGTH = 4096

/** Depth past which nesting is replaced wholesale, bounding both cost and output size. */
const MAX_DEPTH = 6

export const REDACTED = '[redacted]'
export const TRUNCATED = '[truncated]'

export const isPiiKey = (key: string) => {
  const normalized = key.toLowerCase().replace(/[^a-z]/g, '')
  return PII_TOKENS.some(token => normalized.includes(token))
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

/**
 * Recursively strips values whose key marks them as personal data, at every depth.
 *
 * Depth matters because the codebase's dominant idiom nests the sensitive data under a non-PII
 * key — `failure(error, { payload: { session, auth } })`. A top-level-only filter passes the
 * `payload` key and serializes the `session`/`auth` objects whole; descending every level is what
 * redacts them.
 *
 * Non-plain values (class instances, `Error`s, `Date`s) are not descended into: their shape is
 * not a `Record`, and stringifying them is the caller's choice. They are length-bounded instead.
 */

const redact = (value: unknown, depth: number): unknown => {
  if (depth > MAX_DEPTH) return TRUNCATED

  if (Array.isArray(value)) {
    return value.map(entry => redact(entry, depth + 1))
  }

  if (isPlainObject(value)) {
    const result: Record<string, unknown> = {}
    for (const [key, entry] of Object.entries(value)) {
      result[key] = isPiiKey(key) ? REDACTED : redact(entry, depth + 1)
    }
    return result
  }

  if (typeof value === 'string' && value.length > MAX_SERIALIZED_LENGTH) {
    return `${value.slice(0, MAX_SERIALIZED_LENGTH)}${TRUNCATED}`
  }

  return value
}

/**
 * Bounds a serialized value, so one oversized field cannot dominate a log line.
 *
 * Applied after redaction: a value whose serialized form exceeds the cap is replaced by a
 * truncated string rather than emitted whole.
 */

const capSize = (value: unknown) => {
  let serialized: string
  try {
    serialized = JSON.stringify(value) ?? ''
  } catch {
    return TRUNCATED
  }

  if (serialized.length <= MAX_SERIALIZED_LENGTH) return value

  return `${serialized.slice(0, MAX_SERIALIZED_LENGTH)}${TRUNCATED}`
}

/**
 * Prepares result context for logging: personal data removed at every depth, size bounded.
 *
 * `agent-rules/logging.md` requires this before an error or its context reaches a log.
 */
export const sanitizeContext = (context: Record<string, unknown>) => {
  const result: Record<string, unknown> = {}

  for (const [key, value] of Object.entries(context)) {
    result[key] = isPiiKey(key) ? REDACTED : capSize(redact(value, 0))
  }

  return result
}
